import React from 'react';
import { Heart, Wind, Brain, Calendar, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import AllPetCardSection from '../components/AllPetCardSection';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const FeralDogRehab = () => {
  const philosophyPoints = [
    {
      icon: <Brain size={32} />,
      title: 'Nose-Centric Techniques',
      description: 'Using nose-centric training developed by Julie Hart with Heart to Hart training, we tap into a dog\'s most powerful sense. By encouraging a dog to use their nose to explore, we shift their brain from the "fear center" to the "thinking center." If they can sniff it, they can process it.'
    },
    {
      icon: <Wind size={32} />,
      title: 'The Power of Nature',
      description: 'Our farm provides a low-pressure, natural environment. Nature acts as a silent co-trainer, offering calming sights, sounds, and textures that help decompress a stressed nervous system far better than any sterile indoor facility.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Confidence Over Compliance',
      description: 'Our goal isn\'t a dog that follows orders perfectly; it\'s a dog that looks at the world and thinks, "I\'m okay" instead of looking for a place to hide.'
    }
  ];

  const programs = [
    {
      icon: <Calendar size={28} />,
      title: 'Monthly Board & Train',
      description: 'Intensive daily rehab and nature-based socialization. We take it one month at a time to work at your dog\'s pace and not rush them.',
      bestFor: 'Dogs with extreme fear or feral backgrounds who need 24/7 immersion in a safe, home-like environment.'
    },
    {
      icon: <User size={28} />,
      title: 'Private Lessons',
      description: 'One-on-one coaching at the farm. You\'ll work alongside us to learn how to read your dog\'s body language and master nose-work drills.',
      bestFor: 'Owners who want to be hands-on in the rehabilitation process and have a "safe" home environment already established.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Feral Dog Rehabilitation Oklahoma — Fearful & Scared Dog Training | Tulsa, Broken Arrow, Owasso</title>
        <meta name="description" content="Specialized feral dog rehabilitation in Oklahoma. We use nose-centric techniques and nature-based training to help fearful, feral, and scared dogs build confidence and trust. Monthly board & train and private lessons available. Serving Tulsa, Broken Arrow, Owasso, Jenks, Bixby, Sand Springs, Claremore, Sapulpa, Bartlesville, Muskogee, and all surrounding Oklahoma cities." />
        <link rel="canonical" href="https://farmdogoklahoma.com/feral-dog-rehab" />
        <meta property="og:title" content="Feral Dog Rehabilitation — Specialized Fear & Trauma Recovery | Farm Dog Oklahoma" />
        <meta property="og:description" content="Specialized rehabilitation for feral, fearful, and scared dogs in Oklahoma. Nose-centric techniques, nature-based training, home environment." />
        <meta property="og:url" content="https://farmdogoklahoma.com/feral-dog-rehab" />
        <meta name="keywords" content="feral dog rehabilitation Oklahoma, feral dog trainer Tulsa, fearful dog training Oklahoma, scared dog rehabilitation Tulsa, dog behaviorist Oklahoma, feral dog rehab near me, dog fear rehabilitation, feral dog board and train, fearful dog training Broken Arrow, scared dog training Owasso, dog rehabilitation Jenks, dog rehabilitation Bixby, dog anxiety training Oklahoma, dog trauma recovery, nose-centric dog training, nature-based dog rehabilitation" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Feral Dog Rehabilitation Program",
            "provider": { "@type": "LocalBusiness", "name": "Farm Dog Oklahoma", "telephone": "+1-918-992-7332" },
            "description": "Specialized feral dog rehabilitation using nose-centric techniques and nature-based training. We help fearful, feral, and scared dogs build confidence and trust through monthly board & train and private lessons.",
            "areaServed": ["Tulsa", "Broken Arrow", "Owasso", "Jenks", "Bixby", "Sand Springs", "Claremore", "Sapulpa", "Bartlesville", "Muskogee", "Oklahoma"],
            "url": "https://farmdogoklahoma.com/feral-dog-rehab"
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is a feral dog?", "acceptedAnswer": { "@type": "Answer", "text": "A feral dog has had little to no positive human interaction during critical developmental stages. Unlike strays, feral dogs view humans as predators and live in constant survival mode." }},
              { "@type": "Question", "name": "How does feral dog rehabilitation work?", "acceptedAnswer": { "@type": "Answer", "text": "We use nose-centric techniques and nature-based training on our farm. By encouraging dogs to use their nose to explore, we shift their brain from the fear center to the thinking center. Our farm provides a low-pressure natural environment." }},
              { "@type": "Question", "name": "How long does feral dog rehabilitation take?", "acceptedAnswer": { "@type": "Answer", "text": "We take it one month at a time with our monthly board & train program, working at your dog's pace. Unlike traditional programs, our dogs stay in our homes and become part of the family." }},
              { "@type": "Question", "name": "Do you offer private lessons for fearful dogs?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer one-on-one coaching at the farm where you work alongside us to learn how to read your dog's body language and master nose-work drills." }}
            ]
          }
        `}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Reclaiming the Wild Heart
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold mb-4">
              Feral Dog Rehabilitation
            </p>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Every dog deserves to feel safe in their own skin. We transform dogs from survival mode to confident companions.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="text-lg text-gray-700 leading-relaxed">
              At Farm Dog Oklahoma, we believe every dog deserves to feel safe in their own skin. Whether your dog was born under a porch or has spent their life hiding in the shadows, we specialize in bridging the gap between "wild" and "home." We transform dogs that struggle to adapt into reliable, confident companions—helping them thrive where the land runs wide.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <img
                src="/assets/images/12lUtv53vojGdU6XDl5WByhvZlnX9fmPC.jpg"
                alt="Feral dog rehabilitation"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover object-center"
              />
            </motion.div>
            <motion.div variants={fadeInUp} className="space-y-6">
              <h2 className="text-4xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                What is a Feral Dog?
              </h2>
              <p className="text-gray-700 leading-relaxed">
                In the world of rehabilitation, a feral dog is one that has had little to no positive human interaction during their critical developmental stages. Unlike a stray dog, who may have once had a home, a feral dog views humans as predators. They live in a constant state of "survival mode," where their primary instinct is to flee, hide, or protect themselves from the unknown.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our mission is to help these dogs transition from a life governed by fear to a life defined by connection.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Philosophy Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Philosophy: Nature, Noses, and Connection
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We don't focus on "sit, stay, or heel" in the way you might expect. When a dog is paralyzed by fear, traditional obedience is often just another layer of pressure they cannot handle. Instead, we focus on Real-World Skills.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-8">
            {philosophyPoints.map((point, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="hover-lift border-none shadow-lg">
                  <CardContent className="p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                      {point.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#5C4033] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {point.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{point.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Rehabilitation Programs */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Rehabilitation Programs
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We offer two pathways to help your dog find their footing. Unlike traditional programs where your dog stays in a kennel for months, our dogs stay in our homes and become part of the family.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="hover-lift border-none shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white">
                        {program.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {program.title}
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">{program.description}</p>
                    <div className="bg-[#F5F5F0] rounded-lg p-4">
                      <h4 className="font-semibold text-[#C4A052] mb-2">Best For:</h4>
                      <p className="text-gray-700 text-sm">{program.bestFor}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* From Hiding to Thriving */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-gradient-to-br from-[#F5F5F0] to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              From Hiding to Thriving
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Watching a dog move from the back of a crate to the middle of a field—tail up and nose down—is the most rewarding transformation on earth. We are here to help you and your dog find that peace.
            </p>
            <div className="inline-block bg-white rounded-2xl shadow-xl p-8">
              <p className="text-2xl font-semibold text-[#C4A052] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to start the journey from fear to freedom?
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* All Pet Card Section */}
      <AllPetCardSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Begin the Transformation
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Let's help your feral dog discover safety, confidence, and connection.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contact"
            >
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Book Free Consult <ArrowRight className="ml-2" />
              </Button>
            </a>
            <a href="tel:+19189927332">
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Call (918) 992-7332
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeralDogRehab;
